/**
 * ============================================================
 * DARE — Dynamic Adaptive Risk Engine v3
 * useRiskEngine.js
 *
 * Core insight: position sizing should be driven by
 * "how much am I willing to lose on this trade" not
 * "what % of my drawdown floor am I risking".
 *
 * Formula approach:
 *   1. User sets a dollar risk per trade (or % of DLL)
 *   2. ATR sets the stop distance in dollar terms
 *   3. M_v (VIX ratio) scales risk up/down with market conditions
 *   4. contracts = (dollarRiskPerTrade * Mv) / dollarStopPerContract
 *
 * This produces intuitive, realistic contract counts.
 * ============================================================
 */

import { useMemo } from "react";

// ============================================================
// ASSET REGISTRY
// ============================================================
export const ASSET_REGISTRY = {
  ES: {
    name: "E-mini S&P 500", exchange: "CME",
    tickSize: 0.25, tickValueMini: 12.50, tickValueMicro: 1.25,
    microSymbol: "MES", adrTarget: 60,
  },
  NQ: {
    name: "E-mini NASDAQ-100", exchange: "CME",
    tickSize: 0.25, tickValueMini: 5.00, tickValueMicro: 0.50,
    microSymbol: "MNQ", adrTarget: 250,
  },
  RTY: {
    name: "E-mini Russell 2000", exchange: "CME",
    tickSize: 0.10, tickValueMini: 5.00, tickValueMicro: 0.50,
    microSymbol: "M2K", adrTarget: 28,
  },
  YM: {
    name: "E-mini Dow Jones", exchange: "CBOT",
    tickSize: 1.00, tickValueMini: 5.00, tickValueMicro: 0.50,
    microSymbol: "MYM", adrTarget: 350,
  },
  GC: {
    name: "Gold", exchange: "COMEX",
    tickSize: 0.10, tickValueMini: 10.00, tickValueMicro: 1.00,
    microSymbol: "MGC", adrTarget: 25,
  },
  CL: {
    name: "Crude Oil", exchange: "NYMEX",
    tickSize: 0.01, tickValueMini: 10.00, tickValueMicro: 1.00,
    microSymbol: "MCL", adrTarget: 3.5,
  },
};

// ============================================================
// CONSTANTS
// ============================================================
const SURVIVE_THE_OPEN_MULTIPLIER = 0.67;
const MIN_MICRO_CONTRACTS = 1;
const CONSISTENCY_CAP_RATIO = 0.40;

// ============================================================
// CORE MATH
// ============================================================

/**
 * Distance to Death
 */
export function calcDistanceToDeath(currentBalance, drawdownFloor) {
  const value = currentBalance - drawdownFloor;
  return { value, isLiquidated: value <= 0 };
}

/**
 * Volatility Multiplier
 * Pure VIX ratio. > 1 = calmer than average (can size up).
 * < 1 = more volatile (size down). Capped 0.25–2.0.
 */
export function calcVolatilityMultiplier(currentVix, vix20MA, isMarketOpenWindow = false) {
  if (!vix20MA || vix20MA === 0) return 1;
  const raw = vix20MA / currentVix;
  const capped = Math.min(Math.max(raw, 0.25), 2.0);
  return isMarketOpenWindow ? capped * SURVIVE_THE_OPEN_MULTIPLIER : capped;
}

/**
 * ATR Ratio — current ATR vs instrument average daily range.
 * > 1 = above average volatility. < 1 = below average.
 */
export function calcAtrRatio(atr5, adrTarget) {
  if (!adrTarget || adrTarget === 0) return 1;
  return atr5 / adrTarget;
}

/**
 * Risk Level
 */
export function classifyRiskLevel(Mv, atrRatio) {
  const score = Mv / Math.max(atrRatio, 0.5);
  if (score > 1.1) return "Safe";
  if (score >= 0.7) return "Caution";
  return "High Risk";
}

/**
 * Dollar stop per micro contract at a given ATR fraction.
 *
 * stopFraction = fraction of ATR to use as stop distance.
 *   0.25 = quarter ATR stop (tight, more contracts)
 *   0.5  = half ATR stop (default)
 *   1.0  = full ATR stop (wide, fewer contracts)
 */
export function calcDollarStopPerMicro(atr5, tickSize, tickValueMicro, stopFraction = 0.5) {
  const stopPoints = atr5 * stopFraction;
  const stopTicks = stopPoints / tickSize;
  return stopTicks * tickValueMicro;
}

/**
 * Contract Load — THE CORE SIZING FORMULA (v3)
 *
 * dollarRiskPerTrade = how much $ the trader is willing to lose on one trade
 *                    = dailyLossLimit * riskPerTradeRatio
 *                    (e.g. DLL $1500 * 30% = $450 risk per trade)
 *
 * Mv scales this up/down based on VIX regime.
 *
 * contracts = (dollarRiskPerTrade * Mv) / dollarStopPerMicro
 *
 * Returns fractional micro count. Micro Bridge handles flooring + mini conversion.
 */
export function calcContractLoad(
  dollarRiskPerTrade,
  Mv,
  atr5,
  tickSize,
  tickValueMicro,
  stopFraction = 0.5
) {
  const dollarStop = calcDollarStopPerMicro(atr5, tickSize, tickValueMicro, stopFraction);
  if (dollarStop === 0) return 0;
  return (dollarRiskPerTrade * Mv) / dollarStop;
}

/**
 * Micro Bridge
 * Input: fractional micro count
 * Output: { type, count } — always at least 1 micro if Dd > 0
 * 10+ micros → convert to minis
 */
export function applyMicroBridge(rawMicros, isLiquidated) {
  if (isLiquidated || rawMicros <= 0) return { type: "MICRO", count: 0 };
  const micros = Math.max(Math.floor(rawMicros), MIN_MICRO_CONTRACTS);
  if (micros >= 10) return { type: "MINI", count: Math.floor(micros / 10) };
  return { type: "MICRO", count: micros };
}

/**
 * Consistency Check
 */
export function calcConsistencyCheck(profitTarget, currentDayProfit) {
  const consistencyCap = profitTarget * CONSISTENCY_CAP_RATIO;
  const isBreached = currentDayProfit > consistencyCap;
  const percentUsed = consistencyCap > 0
    ? Math.min((currentDayProfit / consistencyCap) * 100, 999)
    : 0;
  return { consistencyCap, isBreached, percentUsed };
}

// ============================================================
// THE DARE HOOK v3
// ============================================================

/**
 * @param {number}  balance
 * @param {number}  drawdownFloor         — trailing/absolute max loss floor
 * @param {number}  currentVix
 * @param {number}  vix20MA
 * @param {number}  atr5                  — 5-day ATR in points
 * @param {string}  selectedAsset
 * @param {number}  dailyLossLimit        — dollar DLL from prop firm rules
 * @param {number}  [riskPerTradeRatio]   — fraction of DLL to risk per trade (default 0.3 = 30%)
 * @param {number}  [stopFraction]        — ATR fraction for stop (default 0.5)
 * @param {boolean} [isMarketOpenWindow]
 * @param {number}  [profitTarget]
 * @param {number}  [currentDayProfit]
 */
export function useRiskEngine({
  balance,
  drawdownFloor,
  currentVix,
  vix20MA,
  atr5,
  selectedAsset,
  dailyLossLimit = 1500,
  riskPerTradeRatio = 0.30,
  stopFraction = 0.5,
  isMarketOpenWindow = false,
  profitTarget = 0,
  currentDayProfit = 0,
}) {
  return useMemo(() => {
    const asset = ASSET_REGISTRY[selectedAsset];
    if (!asset) return { error: `Unknown asset: ${selectedAsset}`, status: "Error" };

    // 1. Distance to Death
    const { value: Dd, isLiquidated } = calcDistanceToDeath(balance, drawdownFloor);

    if (isLiquidated) {
      return {
        status: "Liquidated", Dd, Mv: 0, atrRatio: 1,
        rawLoad: 0, position: { type: "MICRO", count: 0 },
        riskLevel: "High Risk", dollarRiskPerTrade: 0,
        consistency: calcConsistencyCheck(profitTarget, currentDayProfit),
        asset, isMarketOpenWindow,
      };
    }

    // 2. Volatility Multiplier
    const Mv = calcVolatilityMultiplier(currentVix, vix20MA, isMarketOpenWindow);

    // 3. ATR Ratio
    const atrRatio = calcAtrRatio(atr5, asset.adrTarget);

    // 4. Risk Level
    const riskLevel = classifyRiskLevel(Mv, atrRatio);

    // 5. Dollar risk per trade
    // Cap at remaining D_d to never blow the account in one trade
    const baseDollarRisk = dailyLossLimit * riskPerTradeRatio;
    const dollarRiskPerTrade = Math.min(baseDollarRisk, Dd * 0.5);

    // 6. Contract load
    const rawLoad = calcContractLoad(
      dollarRiskPerTrade, Mv, atr5,
      asset.tickSize, asset.tickValueMicro, stopFraction
    );

    // 7. Micro Bridge
    const position = applyMicroBridge(rawLoad, isLiquidated);

    // 8. Dollar risk at position
    const tickValue = position.type === "MINI" ? asset.tickValueMini : asset.tickValueMicro;
    const stopPoints = atr5 * stopFraction;
    const stopTicks = stopPoints / asset.tickSize;
    const dollarRiskAtStop = position.count * stopTicks * tickValue;

    // 9. Dollar stop per contract (for UI display)
    const dollarStopPerContract = calcDollarStopPerMicro(
      atr5, asset.tickSize, asset.tickValueMicro, stopFraction
    );

    // 10. Consistency Check
    const consistency = calcConsistencyCheck(profitTarget, currentDayProfit);

    return {
      status: "Active",
      riskLevel,
      Dd,
      Mv,
      atrRatio,
      rawLoad,
      position,
      dollarRiskPerTrade,
      dollarRiskAtStop,
      dollarStopPerContract,
      stopFraction,
      consistency,
      asset,
      isMarketOpenWindow,
      inputs: {
        balance, drawdownFloor, currentVix, vix20MA, atr5,
        dailyLossLimit, riskPerTradeRatio, stopFraction,
        profitTarget, currentDayProfit,
      },
    };
  }, [
    balance, drawdownFloor, currentVix, vix20MA, atr5,
    selectedAsset, dailyLossLimit, riskPerTradeRatio,
    stopFraction, isMarketOpenWindow, profitTarget, currentDayProfit,
  ]);
}
